const LIKE_FIELDS = new Set(['publisher', 'brand']);

const PRODUCT_RULE_FIELD_ORDER = [
  'type',
  'publisherId',
  'publisher',
  'brandId',
  'brand',
  'available',
];

const compareRuleKeys = (leftKey, rightKey) => {
  const leftBase = legacyKeyToField(leftKey);
  const rightBase = legacyKeyToField(rightKey);
  const leftIndex = PRODUCT_RULE_FIELD_ORDER.indexOf(leftBase);
  const rightIndex = PRODUCT_RULE_FIELD_ORDER.indexOf(rightBase);
  const leftOrder = leftIndex === -1 ? PRODUCT_RULE_FIELD_ORDER.length : leftIndex;
  const rightOrder = rightIndex === -1 ? PRODUCT_RULE_FIELD_ORDER.length : rightIndex;

  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }

  return leftKey.localeCompare(rightKey);
};

export const getOrderedBranchKeys = (branch) => {
  if (!branch || isObjectEmpty(branch)) {
    return [];
  }

  return Object.keys(branch).sort(compareRuleKeys);
};

const isObjectEmpty = (object) => !object || Object.keys(object).length === 0;

const flattenCondition = (condition) => {
  const predicates = [{
    field: condition.field,
    values: condition.values,
  }];

  if (condition.conditions) {
    condition.conditions.forEach((child) => {
      predicates.push(...flattenCondition(child));
    });
  }

  return predicates;
};

const legacyKeyToOperator = (field, key) => {
  if (key.endsWith('.not')) {
    return LIKE_FIELDS.has(field) ? 'NOT LIKE' : 'NOT IN';
  }

  if (key.endsWith('.like')) {
    return 'LIKE';
  }

  if (LIKE_FIELDS.has(key)) {
    return 'LIKE';
  }

  return 'IN';
};

const legacyKeyToField = (key) => {
  if (key.endsWith('.not')) {
    return key.slice(0, -4);
  }

  if (key.endsWith('.like')) {
    return key.slice(0, -5);
  }

  return key;
};

const legacyKeyToCondition = (key, items) => {
  const field = legacyKeyToField(key);
  const operator = legacyKeyToOperator(field, key);

  return {
    field,
    values: {
      operator,
      items: [...items],
    },
  };
};

const conditionToLegacyKey = (field, operator) => {
  if (operator === 'IN') {
    return field;
  }

  if (operator === 'NOT IN') {
    return `${field}.not`;
  }

  if (operator === 'LIKE') {
    return LIKE_FIELDS.has(field) ? field : `${field}.like`;
  }

  if (operator === 'NOT LIKE') {
    return `${field}.not`;
  }

  return field;
};

const conditionToFlatBranch = (condition) => {
  const predicates = flattenCondition(condition);

  return predicates.reduce((branch, predicate) => {
    const key = conditionToLegacyKey(predicate.field, predicate.values.operator);
    return {
      ...branch,
      [key]: predicate.values.items,
    };
  }, {});
};

const flatBranchToCondition = (flatBranch) => {
  const predicates = getOrderedBranchKeys(flatBranch)
    .map((key) => legacyKeyToCondition(key, flatBranch[key]))
    .filter((predicate) => predicate.values.items.length > 0);

  if (predicates.length === 0) {
    return null;
  }

  const [first, ...rest] = predicates;

  if (rest.length === 0) {
    return first;
  }

  return {
    ...first,
    operator: 'AND',
    conditions: rest,
  };
};

export const isUnrestrictedBranches = (branches) => (
  !branches || branches.length === 0 || branches.every((branch) => isObjectEmpty(branch))
);

export const normalizeBranches = (branches) => {
  if (!branches || branches.length === 0) {
    return [{}];
  }

  return branches;
};

export const rulesToBranches = (rules) => {
  if (!rules || !rules.conditions || rules.conditions.length === 0) {
    return [];
  }

  if (rules.conditions.length === 1 && rules.conditions[0].field === 'tid') {
    return [{
      tid: rules.conditions[0].values.items,
    }];
  }

  return rules.conditions.map((condition) => conditionToFlatBranch(condition));
};

export const branchesToRules = (branches) => {
  const nonEmptyBranches = (branches || []).filter((branch) => !isObjectEmpty(branch));

  if (nonEmptyBranches.length === 0) {
    return null;
  }

  if (nonEmptyBranches.length === 1 && nonEmptyBranches[0].tid) {
    const tid = nonEmptyBranches[0].tid || [];
    if (tid.length === 0) {
      return null;
    }

    return {
      conditions: [{
        field: 'tid',
        values: {
          operator: 'IN',
          items: tid,
        },
      }],
    };
  }

  const conditions = nonEmptyBranches
    .map((branch) => flatBranchToCondition(branch))
    .filter(Boolean);

  if (conditions.length === 0) {
    return null;
  }

  return { conditions };
};

export const mapAccessRightFromApi = (incoming) => ({
  ...incoming,
  branches: rulesToBranches(incoming.rules),
});
